/**
 * Repay with Collateral View
 *
 * Uses Euler swap API with TARGET_DEBT mode to:
 * 1. Withdraw collateral from the collateral vault
 * 2. Swap collateral to debt token via Euler's swap routing
 * 3. Repay debt in the liability vault
 *
 * All operations happen in a single EVC batch transaction.
 */

'use client'

import { E_LEND_CHAIN_ID } from '@/config/euler-contracts'
import { invalidateBalancesCache } from '@/domain/cache'
import { formatPercentage } from '@/domain/common/format'
import { useAssetPrice } from '@/domain/euler/oracle/useAssetPrice'
import { useSubaccountStore } from '@/domain/euler/subaccounts'
import { getSubAccount } from '@/domain/evc'
import { useWaitForTransactionReceiptUniversal } from '@/domain/hooks/useWaitForTransactionReceiptUniversal'
import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { NormalizedUnitNumber, Percentage } from '@/domain/types/NumericValues'
import { Token } from '@/domain/types/Token'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import {
  type EulerSwapResponse,
  SwapperMode,
  useEulerSwapQuoteExtended,
  useSimulatedBestQuote,
  hasSwapAndVerify,
} from '@/domain/euler/swap'
import { SwapErrorDisplay } from '@/features/actions/components/SwapErrorDisplay'
import { TimeoutErrorDisplay } from '@/features/actions/components/TimeoutErrorDisplay'
import { ActionRow } from '@/features/actions/components/action-row/ActionRow'
import { useActionTimeout } from '@/features/actions/logic/useActionTimeout'
import { DialogPanel } from '@/features/dialogs/common/components/DialogPanel'
import { MultiPanelDialog } from '@/features/dialogs/common/components/MultiPanelDialog'
import { TransactionOverviewDetailsItem } from '@/features/dialogs/common/components/transaction-overview/TransactionOverviewDetailsItem'
import { isUserRejection } from '@/lib/errors'
import { assets } from '@/ui/assets'
import { getTokenImage } from '@/ui/assets/tokens'
import { Slider } from '@/ui/atoms/slider/Slider'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { BigBlocksBanner } from '@/ui/molecules/big-blocks-banner/BigBlocksBanner'
import { SlippagePanel } from '@/ui/molecules/slippage-panel/SlippagePanel'
import { normalizeDecimalInput } from '@/utils/decimalInput'
import { useDebounce } from '@/utils/useDebounce'
import {
  type DisableControllerTransactionItem,
  type RepayTransactionItem,
  type RepayWithSharesTransactionItem,
  type SwapMulticallTransactionItem,
  type SwapVerifierDebtMaxTransaction,
  type TransactionItems,
  TransactionLabel,
  type TransferTransactionItem,
  type WithdrawTransactionItem,
  useSimulateEVCBatchCall,
} from '@eulerxyz/wagmi-evc'
import { useQueryClient } from '@tanstack/react-query'
import { eVaultImplementationAbi } from 'abis/evm/eVaultImplementation'
import { Check, ChevronDown, Settings } from 'lucide-react'
import Image from 'next/image'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { type Address, erc20Abi, formatUnits, maxUint256, parseUnits, zeroAddress } from 'viem'
import { useAccount, useConfig, useReadContract, useWriteContract } from 'wagmi'
import { type CollateralOption } from '../EBorrowUnifiedRepayDialog'
import { RepaySuccessView } from '../components/RepaySuccessView'
import { formatRepayAmount, getPercentFromAmount } from '../logic/repayAmountUtils'

export interface EBorrowRepayWithCollateralViewProps {
  closeDialog: () => void
  // Collateral vault (source) - used when single collateral or as initial selection
  collateralVault: Address
  collateralSymbol: string
  collateralDecimals: number
  collateralAsset: Address
  collateralLogoURI?: string
  collateralBalance: string
  collateralShares?: string
  // Multiple collateral options (if available)
  collateralOptions?: CollateralOption[]
  // Liability vault (target)
  liabilityVault: Address
  liabilitySymbol: string
  liabilityDecimals: number
  liabilityAsset: Address
  liabilityLogoURI?: string
  debtOwed: string
  subAccountId?: number
  onSuccess?: () => void // Optional callback when transaction succeeds
}

interface BuildRepayTransactionsParams {
  collateralVault: Address
  collateralDecimals: number
  collateralAsset: Address
  collateralSymbol: string
  liabilityVault: Address
  liabilityDecimals: number
  liabilityAsset: Address
  liabilitySymbol: string
  address: Address
  subAccountId?: number
  eulerSwapQuote?: EulerSwapResponse | null
  userShares: string
  isFullRepay?: boolean
  // For same-token repay (no swap needed)
  repayAmount?: string
}

/**
 * Build transactions for repaying debt with collateral when tokens are the SAME
 * Two cases:
 * 1. Same vault (collateralVault === liabilityVault) → use repayWithShares directly
 * 2. Different vaults but same token → withdraw to liability vault, then repay
 */
function buildSameTokenRepayTransactions({
  collateralVault,
  collateralDecimals,
  collateralAsset,
  collateralSymbol,
  liabilityVault,
  liabilityDecimals,
  liabilityAsset,
  liabilitySymbol,
  address,
  subAccountId = 1,
  userShares,
  isFullRepay = false,
  repayAmount,
}: BuildRepayTransactionsParams): TransactionItems {
  if (!address || address === zeroAddress || !repayAmount) {
    return []
  }

  const position = getSubAccount(address, subAccountId)
  const transactions: TransactionItems = []

  const collateralAssetInfo = {
    address: collateralAsset,
    name: collateralSymbol,
    symbol: collateralSymbol,
    decimals: collateralDecimals,
  }

  const liabilityAssetInfo = {
    address: liabilityAsset,
    name: liabilitySymbol,
    symbol: liabilitySymbol,
    decimals: liabilityDecimals,
  }

  // Check if collateral and liability are in the SAME vault
  const isSameVault = collateralVault.toLowerCase() === liabilityVault.toLowerCase()

  if (isSameVault) {
    // SAME VAULT: Use repayWithShares - directly converts shares to repay debt
    const repayWithSharesTx: RepayWithSharesTransactionItem = {
      label: TransactionLabel.REPAYWITHSHARES,
      vault: liabilityVault,
      asset: liabilityAssetInfo,
      from: position,
      to: position, // Receiver of any excess
      value: isFullRepay ? maxUint256.toString() : repayAmount,
      isMax: isFullRepay,
    }
    transactions.push(repayWithSharesTx)
  } else {
    // DIFFERENT VAULTS but same token: withdraw from collateral vault to liability vault, then repay

    // 1. Withdraw collateral TO the liability vault (same token, so it goes directly)
    // Always withdraw exact amount needed, not max - excess would be stuck in liability vault
    const withdrawToLiabilityVault: WithdrawTransactionItem = {
      label: TransactionLabel.WITHDRAW,
      vault: collateralVault,
      asset: collateralAssetInfo,
      from: position,
      to: liabilityVault, // Send directly to liability vault for repay
      value: repayAmount,
      userShares,
      isMax: false,
    }
    transactions.push(withdrawToLiabilityVault)

    // 2. Repay the debt by calling repay on the liability vault
    // The skim will pick up the tokens we just sent
    const repayTx: RepayTransactionItem = {
      label: TransactionLabel.REPAY,
      vault: liabilityVault,
      asset: liabilityAssetInfo,
      from: position,
      to: position,
      value: isFullRepay ? maxUint256.toString() : repayAmount,
      isMax: isFullRepay,
    }
    transactions.push(repayTx)
  }

  // When doing a full repay, disable the controller to close the position
  if (isFullRepay) {
    const disableControllerTx: DisableControllerTransactionItem = {
      label: TransactionLabel.DISABLE_CONTROLLER,
      vault: liabilityVault,
      asset: liabilityAssetInfo,
      from: position,
      to: position,
    }
    transactions.push(disableControllerTx)

    // Transfer remaining collateral shares to main account (sub 0) so user can use it for other positions
    // Skip if same vault (shares are used for repay) or if already on sub-account 0 (would be a self-transfer)
    const mainAccount = getSubAccount(address, 0)
    if (!isSameVault && position.toLowerCase() !== mainAccount.toLowerCase()) {
      const transferCollateralTx: TransferTransactionItem = {
        label: TransactionLabel.TRANSFER,
        vault: collateralVault,
        asset: collateralAssetInfo,
        from: position,
        to: mainAccount,
        value: maxUint256.toString(), // Transfer all remaining shares
        sharesValue: maxUint256.toString(),
        isMax: true, // Use transferFromMax to get all remaining shares
      }
      transactions.push(transferCollateralTx)
    }
  }

  return transactions
}

/**
 * Build transactions for repaying debt with collateral when tokens are DIFFERENT
 * (Requires swap via Euler's swap routing)
 */
function buildSwapRepayTransactions({
  collateralVault,
  collateralDecimals,
  collateralAsset,
  collateralSymbol,
  liabilityVault,
  liabilityDecimals,
  liabilityAsset,
  liabilitySymbol,
  address,
  subAccountId = 1,
  eulerSwapQuote,
  userShares,
  isFullRepay = false,
}: BuildRepayTransactionsParams): TransactionItems {
  if (!address || address === zeroAddress) {
    return []
  }

  if (!hasSwapAndVerify(eulerSwapQuote)) {
    return []
  }

  const swap = eulerSwapQuote.swap
  const verify = eulerSwapQuote.verify

  const position = getSubAccount(address, subAccountId)

  const transactions: TransactionItems = []

  const collateralAssetInfo = {
    address: collateralAsset,
    name: collateralSymbol,
    symbol: collateralSymbol,
    decimals: collateralDecimals,
  }

  const liabilityAssetInfo = {
    address: liabilityAsset,
    name: liabilitySymbol,
    symbol: liabilitySymbol,
    decimals: liabilityDecimals,
  }

  // 1. Withdraw collateral TO the swapper contract
  const withdrawToSwapper: WithdrawTransactionItem = {
    label: TransactionLabel.WITHDRAW,
    vault: collateralVault,
    asset: collateralAssetInfo,
    from: position,
    to: swap.swapperAddress,
    value: eulerSwapQuote.amountIn,
    userShares,
    isMax: false,
  }
  transactions.push(withdrawToSwapper)

  // 2. Swapper.multicall() - executes the swap via Euler's swap routing
  const swapMulticall: SwapMulticallTransactionItem = {
    label: TransactionLabel.SWAP_MULTICALL,
    from: position,
    to: position,
    asset: collateralAssetInfo,
    fromVault: collateralVault,
    toVault: liabilityVault,
    accountTo: position,
    receiver: liabilityVault,
    assetIn: collateralAssetInfo,
    assetOut: liabilityAssetInfo,
    amountIn: eulerSwapQuote.amountIn,
    amountOut: eulerSwapQuote.amountOut,
    swapperAddress: swap.swapperAddress,
    swapperData: swap.swapperData,
    calls: swap.multicallItems.map((item) => item.data),
  }
  transactions.push(swapMulticall)

  // 3. SwapVerifier.verifyDebtMax() - verify debt is below target and repay
  const swapVerify: SwapVerifierDebtMaxTransaction = {
    label: TransactionLabel.SWAP_VERIFIER_DEBT_MAX,
    from: position,
    to: position,
    asset: liabilityAssetInfo,
    vault: verify.vault,
    account: verify.account,
    amount: verify.amount,
    deadline: verify.deadline.toString(),
    verifierAddress: verify.verifierAddress,
    verifierData: verify.verifierData,
  }
  transactions.push(swapVerify)

  // 4. When doing a full repay, disable the controller to close the position
  if (isFullRepay) {
    const disableControllerTx: DisableControllerTransactionItem = {
      label: TransactionLabel.DISABLE_CONTROLLER,
      vault: liabilityVault,
      asset: liabilityAssetInfo,
      from: position,
      to: position,
    }
    transactions.push(disableControllerTx)

    // 5. Transfer remaining collateral shares to main account (sub 0) so user can use it for other positions
    // Skip if already on sub-account 0 (would be a self-transfer)
    const mainAccount = getSubAccount(address, 0)
    if (position.toLowerCase() !== mainAccount.toLowerCase()) {
      const transferCollateralTx: TransferTransactionItem = {
        label: TransactionLabel.TRANSFER,
        vault: collateralVault,
        asset: collateralAssetInfo,
        from: position,
        to: mainAccount,
        value: maxUint256.toString(), // Transfer all remaining shares
        sharesValue: maxUint256.toString(),
        isMax: true, // Use transferFromMax to get all remaining shares
      }
      transactions.push(transferCollateralTx)
    }
  }

  return transactions
}

export function EBorrowRepayWithCollateralView({
  closeDialog,
  collateralVault: initialCollateralVault,
  collateralSymbol: initialCollateralSymbol,
  collateralDecimals: initialCollateralDecimals,
  collateralAsset: initialCollateralAsset,
  collateralLogoURI: initialCollateralLogoURI,
  collateralOptions,
  liabilityVault,
  liabilitySymbol,
  liabilityDecimals,
  liabilityAsset,
  liabilityLogoURI,
  debtOwed,
  subAccountId = 1,
  onSuccess,
}: EBorrowRepayWithCollateralViewProps) {
  const { address } = useAccount()
  const invalidateSubaccounts = useSubaccountStore((state) => state.invalidate)

  // Collateral selector state
  const [isCollateralDropdownOpen, setIsCollateralDropdownOpen] = useState(false)
  const [selectedCollateralIndex, setSelectedCollateralIndex] = useState(0)

  // Determine if we have multiple collateral options
  const hasMultipleCollaterals = collateralOptions && collateralOptions.length > 1

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setIsCollateralDropdownOpen(false)
    if (isCollateralDropdownOpen) {
      document.addEventListener('click', handleClickOutside)
      return () => document.removeEventListener('click', handleClickOutside)
    }
  }, [isCollateralDropdownOpen])

  // Get the active collateral (from options if available, otherwise from props)
  const activeCollateral = useMemo(() => {
    if (collateralOptions && collateralOptions.length > 0) {
      // Safe: we've already checked length > 0
      const selected = collateralOptions[selectedCollateralIndex] ?? collateralOptions[0]!
      return {
        vault: selected.vaultAddress,
        symbol: selected.symbol,
        decimals: selected.decimals,
        asset: selected.assetAddress,
        logoURI: selected.logoURI,
      }
    }
    return {
      vault: initialCollateralVault,
      symbol: initialCollateralSymbol,
      decimals: initialCollateralDecimals,
      asset: initialCollateralAsset,
      logoURI: initialCollateralLogoURI,
    }
  }, [
    collateralOptions,
    selectedCollateralIndex,
    initialCollateralVault,
    initialCollateralSymbol,
    initialCollateralDecimals,
    initialCollateralAsset,
    initialCollateralLogoURI,
  ])

  // Use active collateral values
  const collateralVault = activeCollateral.vault
  const collateralSymbol = activeCollateral.symbol
  const collateralDecimals = activeCollateral.decimals
  const collateralAsset = activeCollateral.asset

  const position = useMemo(() => {
    if (!address || address === zeroAddress) return zeroAddress
    return getSubAccount(address, subAccountId)
  }, [address, subAccountId])

  // Fetch the actual collateral balance (shares) for this specific subaccount
  const { data: subaccountCollateralShares } = useReadContract({
    address: collateralVault,
    abi: erc20Abi,
    functionName: 'balanceOf',
    args: [position],
    chainId: E_LEND_CHAIN_ID,
    query: {
      enabled: !!position && position !== zeroAddress,
    },
  })

  const collateralShares = subaccountCollateralShares || 0n

  // Fetch convertToAssets from the vault to calculate asset value from shares
  const { data: collateralAssetsFromShares } = useReadContract({
    address: collateralVault,
    abi: eVaultImplementationAbi,
    functionName: 'convertToAssets',
    args: [collateralShares],
    chainId: E_LEND_CHAIN_ID,
    query: {
      enabled: collateralShares > 0n,
    },
  })

  // Available collateral in asset units
  const availableCollateralAssets = collateralAssetsFromShares || 0n

  // State for repay percentage
  const [repayPercent, setRepayPercent] = useState(100)
  const [sliderRepayPercent, setSliderRepayPercent] = useState(repayPercent)
  const [isSliderDragging, setIsSliderDragging] = useState(false)
  const [slippage, setSlippage] = useState<Percentage>(Percentage(0.005))
  const pendingRepayPercentRef = useRef<number | null>(null) // Pending value for debounced commit
  const [repayInputValue, setRepayInputValue] = useState('')
  const [isRepayInputFocused, setIsRepayInputFocused] = useState(false)
  const [maxEstimateLockKey, setMaxEstimateLockKey] = useState<string | null>(null)

  // Debounce the slider dragging state to handle keyboard navigation
  // This prevents RPC spam when using arrow keys on the slider
  const { isDebouncing: isSliderDebouncing } = useDebounce(isSliderDragging, String(isSliderDragging), { delay: 300 })
  const { debouncedValue: debouncedRepayPercent, isDebouncing: isRepayInputDebouncing } = useDebounce(
    repayPercent,
    String(repayPercent),
    { delay: 500 },
  )

  // Check if tokens are different
  const needsSwap = useMemo(
    () => Boolean(collateralAsset && liabilityAsset && collateralAsset.toLowerCase() !== liabilityAsset.toLowerCase()),
    [collateralAsset, liabilityAsset],
  )

  // Slider is active if currently dragging OR still in debounce period
  const isSliderActive = isSliderDragging || isSliderDebouncing
  const quoteRepayPercent = isRepayInputFocused ? debouncedRepayPercent : repayPercent
  const isInputQuoteDebouncing = needsSwap && isRepayInputFocused && isRepayInputDebouncing

  useEffect(() => {
    setSliderRepayPercent(repayPercent)
  }, [repayPercent])

  // Commit pending value when slider activity ends (handles both pointer and keyboard)
  useEffect(() => {
    if (!isSliderActive && pendingRepayPercentRef.current !== null) {
      setRepayPercent(pendingRepayPercentRef.current)
      pendingRepayPercentRef.current = null
    }
  }, [isSliderActive])

  // Calculate debt to repay based on percentage
  const debtToRepay = useMemo(() => {
    const debt = Number(debtOwed) || 0
    return (debt * repayPercent) / 100
  }, [debtOwed, repayPercent])

  const quoteDebtToRepay = useMemo(() => {
    const debt = Number(debtOwed) || 0
    return (debt * quoteRepayPercent) / 100
  }, [debtOwed, quoteRepayPercent])

  const debtToRepayBigInt = useMemo(
    () => parseUnits(debtToRepay.toFixed(liabilityDecimals), liabilityDecimals),
    [debtToRepay, liabilityDecimals],
  )
  const quoteDebtToRepayBigInt = useMemo(
    () => parseUnits(quoteDebtToRepay.toFixed(liabilityDecimals), liabilityDecimals),
    [quoteDebtToRepay, liabilityDecimals],
  )

  // Current debt in base units
  const currentDebtBigInt = useMemo(() => {
    if (!debtOwed) return 0n
    const debtNum = Number(debtOwed)
    if (Number.isNaN(debtNum) || debtNum === 0) return 0n
    return parseUnits(debtNum.toFixed(liabilityDecimals), liabilityDecimals)
  }, [debtOwed, liabilityDecimals])

  // Target debt after repay for quote-driving state
  const quoteTargetDebtBigInt = useMemo(() => {
    if (quoteRepayPercent >= 100) return 0n
    return currentDebtBigInt - quoteDebtToRepayBigInt
  }, [currentDebtBigInt, quoteDebtToRepayBigInt, quoteRepayPercent])

  // Get Euler swap quote using TARGET_DEBT mode
  // Disable while slider is active to prevent spamming RPC
  const swapQueryEnabled = Boolean(
    needsSwap &&
      address &&
      address !== zeroAddress &&
      position !== zeroAddress &&
      quoteDebtToRepayBigInt > 0n &&
      !isSliderActive &&
      !isInputQuoteDebouncing,
  )

  const {
    quote: eulerSwapQuote,
    quotes: allQuotes,
    isLoading: isEulerQuoteLoading,
    isRefetching: isQuoteRefetching,
    error: eulerQuoteError,
    refetch: refetchQuotes,
  } = useEulerSwapQuoteExtended({
    tokenIn: needsSwap ? collateralAsset : undefined,
    tokenOut: needsSwap ? liabilityAsset : undefined,
    amount: needsSwap ? quoteDebtToRepayBigInt.toString() : undefined, // Amount to repay (liability amount)
    vaultIn: needsSwap ? collateralVault : undefined,
    receiver: needsSwap ? liabilityVault : undefined,
    accountIn: needsSwap ? position : undefined,
    accountOut: needsSwap ? position : undefined,
    origin: address,
    dustAccount: address, // Add dustAccount for leftover handling
    slippage: Number(slippage.toFixed(4)) * 100,
    enabled: swapQueryEnabled,
    swapperMode: SwapperMode.TARGET_DEBT,
    targetDebt: quoteTargetDebtBigInt.toString(),
    currentDebt: currentDebtBigInt.toString(),
    isRepay: true,
  })

  // Build swap-path transactions for a given quote (used by useSimulatedBestQuote)
  const buildSwapTransactionsForQuote = useCallback(
    (quote: EulerSwapResponse): TransactionItems => {
      if (!address || address === zeroAddress) return []
      if (!collateralVault || !collateralAsset || !liabilityVault || !liabilityAsset) return []

      return buildSwapRepayTransactions({
        collateralVault,
        collateralDecimals,
        collateralAsset,
        collateralSymbol,
        liabilityVault,
        liabilityDecimals,
        liabilityAsset,
        liabilitySymbol,
        address,
        subAccountId,
        userShares: collateralShares.toString(),
        isFullRepay: quoteRepayPercent >= 100,
        eulerSwapQuote: quote,
      })
    },
    [
      address,
      collateralVault,
      collateralDecimals,
      collateralAsset,
      collateralSymbol,
      liabilityVault,
      liabilityDecimals,
      liabilityAsset,
      liabilitySymbol,
      subAccountId,
      collateralShares,
      quoteRepayPercent,
    ],
  )

  // Simulate all swap quotes in parallel (only when swap is needed)
  const {
    quote: validatedSwapQuote,
    transactions: validatedSwapTransactions,
    isSimulating: isValidatingQuotes,
    error: validationError,
    refetch: refetchSimulation,
  } = useSimulatedBestQuote({
    quotes: allQuotes,
    buildTransactions: buildSwapTransactionsForQuote,
    queryKeyDeps: [
      position,
      collateralVault,
      liabilityVault,
      quoteTargetDebtBigInt.toString(),
      currentDebtBigInt.toString(),
      quoteRepayPercent,
    ],
    enabled:
      needsSwap &&
      allQuotes.length > 0 &&
      !!address &&
      address !== zeroAddress &&
      !isSliderActive &&
      !isInputQuoteDebouncing,
  })

  const handleRefetchQuotes = useCallback(() => {
    refetchQuotes()
    refetchSimulation()
  }, [refetchQuotes, refetchSimulation])

  // Use the validated swap quote for display. Don't fall back to the unvalidated API quote
  // when validation failed — showing details of a quote proven to revert is misleading.
  const displayQuote = validatedSwapQuote ?? (validationError ? null : eulerSwapQuote)
  const collateralEstimateQuote = validatedSwapQuote ?? eulerSwapQuote

  // Build the final transaction items
  // For same-token: build directly (no swap quote needed)
  // For swap: use validated transactions from useSimulatedBestQuote
  const transactions = useMemo(() => {
    if (!address || address === zeroAddress) return []
    if (!collateralVault || !collateralAsset || !liabilityVault || !liabilityAsset) return []

    // Same token - no swap needed, direct repay
    if (!needsSwap) {
      return buildSameTokenRepayTransactions({
        collateralVault,
        collateralDecimals,
        collateralAsset,
        collateralSymbol,
        liabilityVault,
        liabilityDecimals,
        liabilityAsset,
        liabilitySymbol,
        address,
        subAccountId,
        userShares: collateralShares.toString(),
        isFullRepay: repayPercent >= 100,
        repayAmount: debtToRepayBigInt.toString(),
      })
    }

    // Different tokens - use validated swap transactions
    return validatedSwapTransactions
  }, [
    address,
    collateralVault,
    collateralDecimals,
    collateralAsset,
    collateralSymbol,
    liabilityVault,
    liabilityDecimals,
    liabilityAsset,
    liabilitySymbol,
    subAccountId,
    collateralShares,
    repayPercent,
    needsSwap,
    debtToRepayBigInt,
    validatedSwapTransactions,
  ])

  const collateralImage = getTokenImage(TokenSymbol(collateralSymbol)) || activeCollateral.logoURI
  const liabilityImage = getTokenImage(TokenSymbol(liabilitySymbol)) || liabilityLogoURI

  // Calculate collateral needed
  // For same-token repay: collateral needed = debt to repay (1:1)
  // For swap repay: use the quote's amountIn
  const collateralNeededRaw = useMemo(() => {
    if (!needsSwap) {
      // Same token - collateral needed equals debt to repay
      return debtToRepayBigInt
    }
    // Different tokens - use the best available quote estimate for collateral sufficiency.
    return collateralEstimateQuote ? BigInt(collateralEstimateQuote.amountIn) : 0n
  }, [needsSwap, debtToRepayBigInt, collateralEstimateQuote])
  // For display purposes only
  const collateralNeeded = Number(formatUnits(collateralNeededRaw, collateralDecimals))

  // Compute display values - use slider value during drag for immediate visual feedback
  const displayDebtToRepay = useMemo(() => {
    if (!isSliderActive) return debtToRepay
    const debt = Number(debtOwed) || 0
    return (debt * sliderRepayPercent) / 100
  }, [isSliderActive, debtToRepay, debtOwed, sliderRepayPercent])
  const displayRemainingDebt = Math.max(0, Number(debtOwed) - displayDebtToRepay)
  const maxDebtOwed = Number(debtOwed) || 0
  const formattedSliderRepayPercent =
    sliderRepayPercent > 0 && sliderRepayPercent < 1 ? sliderRepayPercent.toFixed(2) : sliderRepayPercent.toFixed(0)
  const { priceUSD: liabilityPriceUSD } = useAssetPrice({
    assetAddress: liabilityAsset,
    vaultAddress: liabilityVault,
  })
  const repayValueUSD = displayDebtToRepay * liabilityPriceUSD

  useEffect(() => {
    if (!isRepayInputFocused) {
      setRepayInputValue(formatRepayAmount(displayDebtToRepay))
    }
  }, [displayDebtToRepay, isRepayInputFocused])

  const handleRepayInputChange = (value: string) => {
    pendingRepayPercentRef.current = null
    setRepayInputValue(value)
    const numericValue = Math.min(Number(value) || 0, maxDebtOwed)
    const percent = getPercentFromAmount({ amount: numericValue, maxRepayAmount: maxDebtOwed, safetyMultiplier: 1 })
    setRepayPercent(percent)
  }

  const exactMaxRepayAmount = useMemo(() => {
    if (needsSwap) return 0
    return Math.min(maxDebtOwed, Number(formatUnits(availableCollateralAssets, collateralDecimals)))
  }, [needsSwap, maxDebtOwed, availableCollateralAssets, collateralDecimals])

  const estimatedMaxRepayAmount = useMemo(() => {
    if (!needsSwap || !collateralEstimateQuote || quoteDebtToRepayBigInt <= 0n) return 0

    const conservativeCollateralNeededRaw = BigInt(
      collateralEstimateQuote.amountInMax && collateralEstimateQuote.amountInMax !== '0'
        ? collateralEstimateQuote.amountInMax
        : collateralEstimateQuote.amountIn,
    )

    if (conservativeCollateralNeededRaw <= 0n) return 0

    const rawEstimatedMaxRepayAmount =
      (availableCollateralAssets * quoteDebtToRepayBigInt) / conservativeCollateralNeededRaw

    // Apply a 2% safety margin to account for worse swap rates at larger sizes
    // (the linear extrapolation from a partial quote overestimates what a full-size swap achieves).
    const safetyMarginBps = 200n
    const bufferedEstimatedMaxRepayAmount =
      (rawEstimatedMaxRepayAmount * (10000n - safetyMarginBps)) / 10000n

    return Number(
      formatUnits(
        bufferedEstimatedMaxRepayAmount > currentDebtBigInt ? currentDebtBigInt : bufferedEstimatedMaxRepayAmount,
        liabilityDecimals,
      ),
    )
  }, [
    needsSwap,
    collateralEstimateQuote,
    quoteDebtToRepayBigInt,
    availableCollateralAssets,
    currentDebtBigInt,
    liabilityDecimals,
  ])

  const canEstimateMaxRepay =
    !needsSwap || (!isInputQuoteDebouncing && !!collateralEstimateQuote && quoteDebtToRepay > 0 && collateralNeeded > 0)

  // Check if user has enough deposited collateral (converted from shares)
  const hasEnoughCollateral = availableCollateralAssets >= collateralNeededRaw
  const canEstimateFullDebtRepay = !needsSwap || estimatedMaxRepayAmount >= maxDebtOwed
  const getMaxEstimateContextKey = useCallback(
    (percent: number) =>
      [
        collateralAsset.toLowerCase(),
        liabilityAsset.toLowerCase(),
        availableCollateralAssets.toString(),
        currentDebtBigInt.toString(),
        selectedCollateralIndex,
        slippage.toFixed(4),
        percent.toFixed(6),
      ].join(':'),
    [collateralAsset, liabilityAsset, availableCollateralAssets, currentDebtBigInt, selectedCollateralIndex, slippage],
  )
  const isMaxEstimateLocked =
    needsSwap &&
    !canEstimateFullDebtRepay &&
    maxEstimateLockKey !== null &&
    maxEstimateLockKey === getMaxEstimateContextKey(repayPercent)
  const maxButtonLabel = canEstimateFullDebtRepay ? 'Max' : 'Max (estimate)'
  const effectiveMaxRepayAmount = needsSwap
    ? canEstimateFullDebtRepay
      ? maxDebtOwed
      : estimatedMaxRepayAmount
    : exactMaxRepayAmount
  const maxRepayPercent = maxDebtOwed > 0 ? Math.min((effectiveMaxRepayAmount / maxDebtOwed) * 100, 100) : 0
  const formattedMaxRepayPercent =
    maxRepayPercent > 0 && maxRepayPercent < 1 ? maxRepayPercent.toFixed(2) : maxRepayPercent.toFixed(0)

  const handleSetMax = () => {
    if (!canEstimateMaxRepay) return

    const maxRepayAmount = needsSwap
      ? canEstimateFullDebtRepay
        ? maxDebtOwed
        : estimatedMaxRepayAmount
      : exactMaxRepayAmount

    const percent = getPercentFromAmount({ amount: maxRepayAmount, maxRepayAmount: maxDebtOwed, safetyMultiplier: 1 })
    setRepayPercent(percent)
    setRepayInputValue(formatRepayAmount(maxRepayAmount))
    setMaxEstimateLockKey(needsSwap && !canEstimateFullDebtRepay ? getMaxEstimateContextKey(percent) : null)
  }

  // Use wagmi-evc's simulation hook (only if user has enough collateral)
  // Disable while slider is active to prevent spamming RPC
  const config = useConfig()
  const queryClient = useQueryClient()
  const {
    data: simulationData,
    error: simulationError,
    isLoading: isSimulating,
  } = useSimulateEVCBatchCall({
    config,
    transactions,
    query: {
      enabled:
        !!address &&
        transactions.length > 0 &&
        (!needsSwap || (!!validatedSwapQuote && !isEulerQuoteLoading && !isInputQuoteDebouncing)) &&
        hasEnoughCollateral &&
        !isSliderActive,
      retry: false,
    },
  })

  // Write contract
  const { writeContract, isPending: isWriting, error: writeError, data: txHash } = useWriteContract()

  // Wait for transaction
  const {
    isLoading: isWaiting,
    isSuccess: isConfirmed,
    error: waitError,
  } = useWaitForTransactionReceiptUniversal({
    hash: txHash,
  })

  // Execute the batch
  const handleExecute = useCallback(async () => {
    if (!simulationData?.request) return

    try {
      writeContract(simulationData.request as Parameters<typeof writeContract>[0])
    } catch {
      // Error handled by writeError
    }
  }, [simulationData, writeContract])

  const repayTimeout = useActionTimeout({
    timeoutMs: 15000,
    isLoading: isWriting || isWaiting || isSimulating,
    isSuccess: isConfirmed,
    error: writeError || waitError || simulationError,
  })

  const handleExecuteWithTimeout = useCallback(() => {
    repayTimeout.handleActionClick(handleExecute)
  }, [repayTimeout, handleExecute])

  const swapError = validationError || simulationError || writeError || waitError || eulerQuoteError

  // Create Token objects for components
  const liabilityToken = useMemo(
    () =>
      new Token({
        address: CheckedAddress(liabilityAsset),
        symbol: TokenSymbol(liabilitySymbol),
        name: liabilitySymbol,
        decimals: liabilityDecimals,
        unitPriceUsd: '0',
        ...(liabilityLogoURI && { logoURI: liabilityLogoURI }),
      }),
    [liabilityAsset, liabilitySymbol, liabilityDecimals, liabilityLogoURI],
  )

  // Determine execute action status based on transaction state
  const isQuoteLoading = needsSwap && (isInputQuoteDebouncing || isEulerQuoteLoading || isValidatingQuotes)
  const executeStatus = useMemo(() => {
    // Only disable for non-user-rejection errors (allow retry after wallet rejection)
    const hasBlockingError =
      (validationError && !isUserRejection(validationError)) ||
      (simulationError && !isUserRejection(simulationError)) ||
      (writeError && !isUserRejection(writeError)) ||
      (waitError && !isUserRejection(waitError))

    if (isConfirmed) return 'success'
    if (isWriting || isWaiting) return 'loading'
    if (isSimulating || isQuoteLoading) return 'loading'
    if (hasBlockingError) return 'disabled'
    if (!simulationData?.request || !hasEnoughCollateral || (needsSwap && !displayQuote)) return 'disabled'
    return 'ready'
  }, [
    isConfirmed,
    isWriting,
    isWaiting,
    isSimulating,
    isQuoteLoading,
    validationError,
    simulationError,
    writeError,
    waitError,
    simulationData?.request,
    hasEnoughCollateral,
    needsSwap,
    displayQuote,
  ])

  // Notify parent when transaction succeeds and invalidate queries
  useEffect(() => {
    if (isConfirmed) {
      onSuccess?.()

      // Invalidate queries to refresh UI
      if (address) {
        invalidateBalancesCache({
          queryClient,
          chainId: E_LEND_CHAIN_ID,
          address,
          position,
          vaultAddress: liabilityVault,
          subAccountAddress: position,
        })
      }
      // Invalidate subaccount cache to trigger discovery refresh
      // This ensures freed subaccounts are detected for reuse
      invalidateSubaccounts()
    }
  }, [isConfirmed, onSuccess, address, position, queryClient, liabilityVault, invalidateSubaccounts])

  // Handle success
  if (isConfirmed) {
    return (
      <RepaySuccessView
        token={liabilityToken}
        amount={NormalizedUnitNumber(debtToRepay.toString())}
        onProceed={closeDialog}
        proceedText="Done"
      />
    )
  }

  return (
    <MultiPanelDialog>
      <DialogPanel>
        {/* Repay Amount Selector */}
        <div className="mb-6 flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-muted-foreground text-sm">Repay Amount</span>
              <span className="text-muted-foreground text-xs">
                {canEstimateMaxRepay
                  ? `Slider max: ${formattedMaxRepayPercent}% of debt${!canEstimateFullDebtRepay ? ' (estimate)' : ''}`
                  : 'Estimating max...'}
              </span>
            </div>
            <span className="font-medium text-lg text-white">{formattedSliderRepayPercent}%</span>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground text-xs">Amount to repay</span>
              <button
                type="button"
                onClick={handleSetMax}
                disabled={!canEstimateMaxRepay || isMaxEstimateLocked}
                className="text-primary text-xs transition-colors disabled:cursor-default disabled:text-muted-foreground hover:text-primary/80"
              >
                {maxButtonLabel}
              </button>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-primary/20 bg-primary/5 p-4">
              <div className="flex items-center gap-3">
                {liabilityImage && (
                  <Image src={liabilityImage} alt={liabilitySymbol} width={32} height={32} className="rounded-full" />
                )}
                <div className="flex flex-col">
                  <span className="text-muted-foreground text-xs">Debt Repaid</span>
                  <span className="font-medium text-sm text-white">{liabilitySymbol}</span>
                </div>
              </div>
              <div className="flex min-w-0 flex-col items-end gap-1">
                <input
                  type="text"
                  inputMode="decimal"
                  value={repayInputValue}
                  onChange={(e) => {
                    const normalized = normalizeDecimalInput(e.target.value)
                    if (normalized === null) return
                    handleRepayInputChange(normalized)
                  }}
                  onFocus={() => setIsRepayInputFocused(true)}
                  onBlur={() => {
                    setIsRepayInputFocused(false)
                    setRepayInputValue(formatRepayAmount(displayDebtToRepay))
                  }}
                  className="min-w-0 bg-transparent text-right font-semibold text-lg text-primary outline-none placeholder:text-primary/40"
                  placeholder="0.00"
                />
                <div className="flex items-center gap-2">
                  {repayValueUSD > 0 && (
                    <span className="text-muted-foreground text-xs">
                      ${repayValueUSD.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
          <Slider
            value={[sliderRepayPercent]}
            onValueChange={([value]) => {
              setIsSliderDragging(true)
              setSliderRepayPercent(value ?? 100)
              pendingRepayPercentRef.current = value ?? 100
            }}
            onValueCommit={([value]) => {
              // Store pending value - actual commit happens via effect when slider activity ends
              pendingRepayPercentRef.current = value ?? 100
            }}
            onPointerDown={() => setIsSliderDragging(true)}
            onPointerUp={() => setIsSliderDragging(false)}
            onLostPointerCapture={() => setIsSliderDragging(false)}
            onKeyUp={() => setIsSliderDragging(false)}
            onBlur={() => setIsSliderDragging(false)}
            max={100}
            step={1}
          />
          <div className="flex justify-between text-muted-foreground text-xs">
            <span>0%</span>
            <span>25%</span>
            <span>50%</span>
            <span>75%</span>
            <span>100%</span>
          </div>
        </div>

        {/* Summary */}
        <div className="flex flex-col gap-4">
          {/* Collateral Used - with dropdown if multiple options */}
          <div className="relative">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                if (hasMultipleCollaterals) setIsCollateralDropdownOpen(!isCollateralDropdownOpen)
              }}
              className={`flex w-full items-center justify-between rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 ${
                hasMultipleCollaterals
                  ? 'cursor-pointer transition-colors hover:border-white/[0.12] hover:bg-white/[0.04]'
                  : ''
              }`}
            >
              <div className="flex items-center gap-3">
                {collateralImage && (
                  <Image src={collateralImage} alt={collateralSymbol} width={32} height={32} className="rounded-full" />
                )}
                <div className="flex flex-col items-start">
                  <span className="text-muted-foreground text-xs">Collateral Used</span>
                  <div className="flex items-center gap-1">
                    <span className="font-medium text-sm text-white">{collateralSymbol}</span>
                    {hasMultipleCollaterals && (
                      <ChevronDown
                        className={`h-4 w-4 text-muted-foreground transition-transform ${isCollateralDropdownOpen ? 'rotate-180' : ''}`}
                      />
                    )}
                  </div>
                </div>
              </div>
              <span className="font-semibold text-lg text-white">
                {isQuoteLoading
                  ? '...'
                  : collateralNeeded.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 6,
                    })}
              </span>
            </button>

            {/* Dropdown menu */}
            {hasMultipleCollaterals && isCollateralDropdownOpen && (
              <div className="absolute top-full left-0 z-50 mt-2 w-full overflow-hidden rounded-xl border border-white/[0.08] bg-[#1a1a1a] shadow-2xl">
                {collateralOptions.map((option, index) => {
                  const isSelected = index === selectedCollateralIndex
                  const optionImage = getTokenImage(TokenSymbol(option.symbol)) || option.logoURI
                  return (
                    <button
                      type="button"
                      key={option.vaultAddress}
                      onClick={(e) => {
                        e.stopPropagation()
                        setSelectedCollateralIndex(index)
                        setIsCollateralDropdownOpen(false)
                      }}
                      className={`flex w-full items-center justify-between px-4 py-3 transition-colors hover:bg-white/[0.06] ${
                        isSelected ? 'bg-primary/10' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <TokenIcon
                          token={{
                            logoURI: optionImage,
                            symbol: TokenSymbol(option.symbol),
                          }}
                          className="h-7 w-7"
                        />
                        <div className="flex flex-col items-start">
                          <span className="font-medium text-sm text-white">{option.symbol}</span>
                          <span className="text-muted-foreground text-xs">
                            Balance:{' '}
                            {Number(option.balance).toLocaleString(undefined, {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 4,
                            })}
                          </span>
                        </div>
                      </div>
                      {isSelected && <Check className="h-4 w-4 text-primary" />}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        {/* Transaction Details */}
        <div className="mt-4 flex flex-col gap-2">
          <TransactionOverviewDetailsItem label="Current Debt">
            {Number(debtOwed).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 6,
            })}{' '}
            {liabilitySymbol}
          </TransactionOverviewDetailsItem>
          <TransactionOverviewDetailsItem label="Remaining Debt">
            {displayRemainingDebt.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 6,
            })}{' '}
            {liabilitySymbol}
          </TransactionOverviewDetailsItem>
          <TransactionOverviewDetailsItem label="Slippage tolerance">
            <SlippagePanel
              slippage={slippage}
              setSlippage={setSlippage}
              title="Swap Slippage"
              trigger={
                <button
                  type="button"
                  className="flex items-center gap-1 text-muted-foreground transition-colors hover:text-white"
                >
                  <span>{formatPercentage(slippage, { minimumFractionDigits: 1 })}</span>
                  <Settings className="h-3.5 w-3.5" />
                </button>
              }
            />
          </TransactionOverviewDetailsItem>
          {displayQuote && (
            <TransactionOverviewDetailsItem label="Swap via">
              <span className="text-green-400">
                EulerSwap
                {displayQuote.route?.length
                  ? ` (${displayQuote.route.map((r: any) => r.providerName).join(' → ')})`
                  : ''}
              </span>
            </TransactionOverviewDetailsItem>
          )}
        </div>

        {/* Loading States */}
        {(isValidatingQuotes || isSimulating) && (
          <div className="mt-4 flex items-center gap-2 text-muted-foreground text-sm">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            Simulating transaction...
          </div>
        )}

        {isQuoteLoading && (
          <div className="mt-4 flex items-center gap-2 text-muted-foreground text-sm">
            <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            Fetching swap quote...
          </div>
        )}

        {/* Warnings */}
        {!hasEnoughCollateral && (!needsSwap || (!!collateralEstimateQuote && !isEulerQuoteLoading)) && (
          <div className="mt-4 rounded-lg border border-yellow-500/20 bg-yellow-500/10 p-3">
            <p className="text-sm text-yellow-400">
              Insufficient collateral. You need{' '}
              {collateralNeeded.toLocaleString(undefined, { maximumFractionDigits: 6 })} {collateralSymbol} but only
              have{' '}
              {Number(formatUnits(availableCollateralAssets, collateralDecimals)).toLocaleString(undefined, {
                maximumFractionDigits: 6,
              })}{' '}
              deposited.
            </p>
          </div>
        )}

        {/* Timeout error display */}
        <TimeoutErrorDisplay
          isTimedOut={repayTimeout.isTimedOut}
          onRetry={handleExecuteWithTimeout}
          isRetryDisabled={isWriting || isWaiting || !simulationData?.request}
        />

        {/* Swap error display */}
        {!repayTimeout.isTimedOut && (
          <SwapErrorDisplay
            error={swapError}
            isLoading={isQuoteLoading}
            onRefetch={handleRefetchQuotes}
            isRefetching={isQuoteRefetching || isValidatingQuotes}
            simulationErrorMessage={`Transaction simulation failed. This usually means you don't have enough deposited ${collateralSymbol} to cover the repayment.`}
            className="mt-4"
          />
        )}

        <BigBlocksBanner className="mt-4" />

        {/* Actions Grid */}
        <div className="mt-6 grid grid-cols-[auto_auto_1fr_auto] gap-x-4">
          <ActionRow index={1}>
            <ActionRow.Icon path={assets.actions.repay} actionStatus={executeStatus} />
            <ActionRow.Title icon={<TokenIcon token={liabilityToken} className="h-6" />} actionStatus={executeStatus}>
              {sliderRepayPercent >= 100 ? 'Close Position' : 'Repay with Collateral'}
            </ActionRow.Title>
            <ActionRow.Description
              successMessage={sliderRepayPercent >= 100 ? 'Position Closed!' : 'Repaid!'}
              actionStatus={executeStatus}
              variant="compact"
            />
            <ActionRow.Action onAction={handleExecuteWithTimeout} status={isSliderActive ? 'disabled' : executeStatus}>
              {isSimulating || isQuoteLoading
                ? 'Preparing...'
                : !hasEnoughCollateral
                  ? 'Insufficient Collateral'
                  : sliderRepayPercent >= 100
                    ? 'Close Position'
                    : 'Repay'}
            </ActionRow.Action>
          </ActionRow>
        </div>
      </DialogPanel>
    </MultiPanelDialog>
  )
}
